import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { EM_DASH } from "@/lib/utils";

import {
  fetchQualityRmaDetail,
  qualityRmaQueryKey,
  updateRmaIntake,
  type QualityRmaDetailResponse,
} from "./rmaApi";

const HELP_TEXT = [
  "Log each device Receiving checked in.",
  "Model comes from the scanned unit. Enter AA voltage and C voltage as numbers, such as 3.65, plus board revision and visual inspection.",
].join(" ");

const VOLTAGE_PATTERN = /^\d+(\.\d{1,3})?$/;

type IntakeRow = QualityRmaDetailResponse["intake"][number];

function voltageValue(raw: string, label: string): string | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  if (!VOLTAGE_PATTERN.test(trimmed)) {
    throw new Error(`${label} must be a number such as 3.65.`);
  }
  return trimmed;
}

function intakeNeedsDetails(row: IntakeRow): boolean {
  return (row.aaVoltage ?? "") === "" && (row.cVoltage ?? "") === "" && (row.visualInspection ?? "") === "";
}

export function QualityQcAnalysisDeskPage(): JSX.Element {
  const params = useParams();
  const rmaId = Number(params.id);
  const writesAllowed = useHubModuleWrite("quality");
  const queryClient = useQueryClient();
  const detailQuery = useQuery({
    queryKey: qualityRmaQueryKey(rmaId),
    queryFn: () => fetchQualityRmaDetail(rmaId),
    enabled: Number.isSafeInteger(rmaId) && rmaId > 0,
  });

  if (!Number.isSafeInteger(rmaId) || rmaId < 1) {
    return <p className="text-sm text-rose-700">This RMA link is not valid.</p>;
  }
  if (detailQuery.isLoading) {
    return <p className="text-sm text-slate-600">Loading RMA…</p>;
  }
  if (detailQuery.isError || detailQuery.data == null) {
    return <p className="text-sm text-rose-700">Could not load this RMA.</p>;
  }

  const detail = detailQuery.data;
  const waiting = detail.intake.filter(intakeNeedsDetails).length;

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint={HELP_TEXT} breadcrumbLabel={detail.rma.rmaNumber}>
        {detail.rma.rmaNumber}
      </PageTitleWithHelp>
      <p className="text-sm text-slate-600">
        {detail.rma.endUserName ?? "No end user"}
        {detail.rma.complaint ? ` · ${detail.rma.complaint}` : ""}
        {" · "}
        <Link className="text-sky-800 hover:underline" to={`/quality/rmas/${detail.rma.id}`}>
          Open the RMA record
        </Link>
      </p>
      {detail.intake.length === 0 ? (
        <Card>
          <CardBody>
            <p className="text-sm text-slate-600">
              Receiving has not checked in any devices for this RMA yet.
            </p>
          </CardBody>
        </Card>
      ) : (
        <>
          <p className="text-sm text-slate-600">
            {detail.intake.length} device{detail.intake.length === 1 ? "" : "s"}
            {waiting > 0 ? `, ${waiting} still need details` : ", details logged"}
          </p>
          {detail.intake.map((row) => (
            <QcAnalysisDeviceCard
              key={`${row.id}:${row.updatedAt}`}
              rmaId={detail.rma.id}
              row={row}
              writesAllowed={writesAllowed}
              onSaved={async (next) => {
                queryClient.setQueryData(qualityRmaQueryKey(rmaId), next);
              }}
            />
          ))}
        </>
      )}
    </div>
  );
}

function QcAnalysisDeviceCard({
  rmaId,
  row,
  writesAllowed,
  onSaved,
}: {
  rmaId: number;
  row: IntakeRow;
  writesAllowed: boolean;
  onSaved: (detail: QualityRmaDetailResponse) => Promise<void>;
}): JSX.Element {
  const { showToast } = useHubToast();
  const [unitModel, setUnitModel] = useState(row.unitModel ?? "");
  const [aaVoltage, setAaVoltage] = useState(row.aaVoltage ?? "");
  const [cVoltage, setCVoltage] = useState(row.cVoltage ?? "");
  const [boardRevision, setBoardRevision] = useState(row.boardRevision ?? "");
  const [visualInspection, setVisualInspection] = useState(row.visualInspection ?? "");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateRmaIntake(rmaId, row.id, {
        unitSerial: row.unitSerial ?? undefined,
        unitModel: unitModel.trim() || undefined,
        aaVoltage: voltageValue(aaVoltage, "AA voltage"),
        cVoltage: voltageValue(cVoltage, "C voltage"),
        boardRevision: boardRevision.trim() || undefined,
        visualInspection: visualInspection.trim() || undefined,
      }),
    onSuccess: (detail) => {
      void onSaved(detail);
      showToast({ tone: "success", message: `Saved ${row.unitSerial ?? "device"}.` });
    },
    onError: (error: unknown) => {
      setErrorMessage(error instanceof Error ? error.message : "Could not save this device.");
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-mono text-base">
          {row.unitSerial ?? EM_DASH}
          {intakeNeedsDetails(row) ? (
            <span className="ml-2 font-sans text-xs font-medium uppercase tracking-wide text-amber-800">
              Needs details
            </span>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor={`qc-model-${row.id}`}>Model</Label>
            <Input
              id={`qc-model-${row.id}`}
              value={unitModel}
              disabled={!writesAllowed}
              onChange={(event) => setUnitModel(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor={`qc-aa-${row.id}`}>AA voltage</Label>
            <Input
              id={`qc-aa-${row.id}`}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              placeholder="3.65"
              value={aaVoltage}
              disabled={!writesAllowed}
              onChange={(event) => setAaVoltage(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor={`qc-c-${row.id}`}>C voltage</Label>
            <Input
              id={`qc-c-${row.id}`}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              placeholder="3.65"
              value={cVoltage}
              disabled={!writesAllowed}
              onChange={(event) => setCVoltage(event.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor={`qc-board-${row.id}`}>Board revision</Label>
            <Input
              id={`qc-board-${row.id}`}
              value={boardRevision}
              disabled={!writesAllowed}
              onChange={(event) => setBoardRevision(event.target.value)}
            />
          </div>
        </div>
        <div>
          <Label htmlFor={`qc-inspection-${row.id}`}>Visual inspection</Label>
          <TextArea
            id={`qc-inspection-${row.id}`}
            rows={5}
            className="min-h-32 font-sans"
            value={visualInspection}
            disabled={!writesAllowed}
            onChange={(event) => setVisualInspection(event.target.value)}
          />
        </div>
        {errorMessage ? <p className="text-sm text-rose-700">{errorMessage}</p> : null}
        {writesAllowed ? (
          <div className="flex justify-end">
            <Button
              isLoading={saveMutation.isPending}
              onClick={() => {
                setErrorMessage(null);
                try {
                  voltageValue(aaVoltage, "AA voltage");
                  voltageValue(cVoltage, "C voltage");
                } catch (error) {
                  setErrorMessage(error instanceof Error ? error.message : "Voltage must be a number such as 3.65.");
                  return;
                }
                saveMutation.mutate();
              }}
            >
              Save device
            </Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
